import React, { useEffect, useState } from "react";
import Header from "../Components/Header/Header";
import axios from "axios";
import Footer from "../Components/Footer/Footer";
import { Calendar, Sparkles, User, MapPin, Trophy, CreditCard, CheckSquare, UploadCloud, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const RathFestRegistration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const [loading, setLoading] = useState(false);
  const [isGroupEvent, setIsGroupEvent] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    age: "",
    gender: "",
    email: "",
    mobile: "",
    whatsApp: "",
    guardianName: "",
    guardianMobile: "",
    city: "",
    district: "",
    schoolCollege: "",
    classGrade: "",
    category: "",
    competitions: [],
    isGroup: "No",
    teamName: "",
    participantsCount: "",
    teamMembers: "",
    declaration1: false,
    declaration2: false,
    declaration3: false,
    paymentMode: "Online",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCompetitionChange = (comp) => {
    setFormData((prevData) => {
      const exists = prevData.competitions.includes(comp);
      if (exists) {
        return {
          ...prevData,
          competitions: prevData.competitions.filter((c) => c !== comp),
        };
      } else {
        return {
          ...prevData,
          competitions: [...prevData.competitions, comp],
        };
      }
    });
  };

  const storeFormData = async (paymentStatus, paymentId) => {
    const storingFormData = {
      paymentStatus: paymentStatus,
      paymentId: paymentId || "",
      paymentMode: formData.paymentMode,
      fullName: formData.fullName,
      dob: formData.dob,
      age: formData.age,
      gender: formData.gender,
      email: formData.email,
      mobile: formData.mobile,
      whatsApp: formData.whatsApp,
      guardianName: formData.guardianName,
      guardianMobile: formData.guardianMobile,
      city: formData.city,
      district: formData.district,
      schoolCollege: formData.schoolCollege,
      classGrade: formData.classGrade,
      category: formData.category,
      competitions: formData.competitions.join(", "),
      isGroup: formData.isGroup,
      teamName: formData.isGroup === "Yes" ? formData.teamName : "",
      participantsCount: formData.isGroup === "Yes" ? formData.participantsCount : "",
      teamMembers: formData.isGroup === "Yes" ? formData.teamMembers : "",
    };

    const formBody = new URLSearchParams(storingFormData).toString();

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbxTSi-QsnzuOi9SIq3p0gFU7KaH2uqYyzA8HRJJqlSjK_djTQZ2DwK6bNIxyiJj_Hx2zg/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formBody,
        }
      );

      console.log("Data stored successfully in Google Sheets");
      return true;
    } catch (error) {
      console.error("Error storing data:", error);
      alert("Some error occurred while saving your registration details.");
      return false;
    }
  };

  const handlePayment = (e) => {
    e.preventDefault();

    if (formData.competitions.length === 0) {
      alert("Please select at least one competition.");
      return;
    }

    setLoading(true);

    const finalAmount = formData.isGroup === "Yes"
      ? (parseInt(formData.participantsCount, 10) || 1) * 108
      : 108;

    if (formData.paymentMode === "Cash") {
      (async () => {
        try {
          const success = await storeFormData("Cash", "Cash Payment");
          if (success) {
            setPaymentId("Cash Payment");
            setShowSuccessModal(true);
          }
        } catch (err) {
          console.error(err);
          alert("Some error occurred while saving your registration details.");
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    try {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: finalAmount * 100,
        currency: "INR",
        name: "Rath Fest 2026",
        description: `Registration Fee - ${formData.isGroup === "Yes" ? "Group" : "Individual"}`,
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.mobile,
        },
        handler: async function (response) {
          try {
            setLoading(true);
            const success = await storeFormData("Payment Successful", response.razorpay_payment_id);
            if (success) {
              setPaymentId(response.razorpay_payment_id);
              setShowSuccessModal(true);
            }
          } catch (err) {
            console.error(err);
            alert("Payment recorded, but data storage encountered an issue. Please contact support.");
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
        theme: {
          color: "#ea580c",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", async function (response) {
        alert("Payment Failed. Reason: " + response.error.description);
        setLoading(true);
        await storeFormData("Payment Failed", "");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      alert("Something went wrong: " + (err.message || err));
      console.error(err);
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setPaymentId("");
    setFormData({
      fullName: "",
      dob: "",
      age: "",
      gender: "",
      email: "",
      mobile: "",
      whatsApp: "",
      guardianName: "",
      guardianMobile: "",
      city: "",
      district: "",
      schoolCollege: "",
      classGrade: "",
      category: "",
      competitions: [],
      isGroup: "No",
      teamName: "",
      participantsCount: "",
      teamMembers: "",
      declaration1: false,
      declaration2: false,
      declaration3: false,
      paymentMode: "Online",
    });
    setIsGroupEvent(false);
  };

  return (
    <>
      <Header />
      <div className="bg-[#FAF7F0] min-h-screen flex flex-col font-open-sans">
        
        {/* Hero Banner Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-darkred via-red to-orange text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 text-center shadow-2xl">
          {/* Animated backgrounds */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08)_0%,transparent_60%)] pointer-events-none" />
          
          {/* Rotating Traditional Mandala Graphic */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.06] select-none pointer-events-none w-[600px] h-[600px] animate-[spin_180s_linear_infinite]">
            <svg viewBox="0 0 100 100" className="w-full h-full text-white fill-current">
              <path d="M50 0 A50 50 0 1 0 50 100 A50 50 0 1 0 50 0 M50 10 A40 40 0 1 1 50 90 A40 40 0 1 1 50 10" />
              <circle cx="50" cy="50" r="5" />
              {[...Array(12)].map((_, i) => (
                <path
                  key={i}
                  d="M50 20 L53 35 L47 35 Z"
                  transform={`rotate(${i * 30} 50 50)`}
                />
              ))}
              {[...Array(24)].map((_, i) => (
                <circle
                  key={i}
                  cx="50"
                  cy="15"
                  r="1.5"
                  transform={`rotate(${i * 15} 50 50)`}
                />
              ))}
            </svg>
          </div>

          {/* Decorative Lotus Bottom Pattern */}
          <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-orange via-yellow-400 to-orange opacity-40 blur-xs" />

          <div className="max-w-4xl mx-auto relative z-10 space-y-6">
            {/* Top Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-xs sm:text-sm tracking-wide uppercase shadow-inner">
              <Sparkles className="w-4 h-4 text-orange animate-pulse" />
              RATH FEST 2026
            </div>

            {/* Heading */}
            <h1 className="font-dm-serif text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white drop-shadow-md">
              Competition <br className="hidden md:block"/>
              <span className="text-yellow-400">Registration Form</span>
            </h1>

            {/* Date Badge */}
            <div className="inline-flex items-center gap-2.5 px-6 py-2.5 rounded-2xl bg-amber-500/25 border border-amber-400/40 text-yellow-300 text-sm md:text-base font-bold shadow-md">
              <Calendar className="w-5 h-5" />
              📅 14th & 15th July 2026
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow w-full">
          {/* Details Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-6 sm:p-10 relative overflow-hidden mb-12">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
            
            <div className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-darkred">Hare Krishna!</h2>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                  Welcome to Rath Fest 2026, a grand Cultural, Educational & Devotional Competition Festival conducted as part of the sacred Rath Yatra celebrations of Lord Jagannath.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 bg-orange/5 px-2 py-3 md:p-6 rounded-2xl border border-orange/10">
                <div className="flex items-start gap-2 md:gap-4">
                  <div className="md:text-2xl text-xl">📅</div>
                  <div>
                    <p className="font-bold text-gray-900 m-0">Date</p>
                    <p className="text-gray-600 font-medium max-sm:text-sm">14th & 15th July 2026</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-4">
                  <div className="md:text-2xl text-xl">💰</div>
                  <div>
                    <p className="font-bold text-gray-900 m-0">Registration Fee</p>
                    <p className="text-gray-600 font-medium max-sm:text-sm">₹108/- per participant</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-4">
                  <div className="md:text-2xl text-xl">📞</div>
                  <div>
                    <p className="font-bold text-gray-900 m-0">Contact</p>
                    <p className="text-gray-600 font-medium max-sm:text-sm">+91 7013127778</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="flex items-center gap-2 text-xl font-bold text-darkred mb-4">
                  <span className="text-2xl">🎁</span> Fee Includes:
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-sm:p-0">
                  {[
                    "Competition Registration",
                    "Participant Kit",
                    "Participation Certificate",
                    "Evaluation by Qualified Judges",
                    "Entry to Rath Fest Activities",
                    "Awards & Recognition"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700 bg-[#FAF9F6] border border-orange/10 shadow-sm px-4 py-3 rounded-xl transition-transform hover:-translate-y-0.5">
                      <div className="w-2 h-2 rounded-full bg-orange flex-shrink-0" />
                      <span className="font-semibold text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Registration Form Header */}
          <div className="text-center mb-8 mt-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-darkred">Registration Form</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-red to-orange mx-auto mt-4 rounded-full"></div>
          </div>

          {/* Registration Form */}
          <form className="space-y-10" onSubmit={handlePayment}>
            <div className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-6 sm:p-10 relative overflow-hidden">
            {/* Section 1: Personal Information */}
              <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-red to-orange" />
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange/10 rounded-xl text-orange">
                  <User className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-darkred">Personal Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Full Name <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="Enter full name"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-bold text-gray-700">Date of Birth <span className="text-red">*</span></label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 text-gray-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-bold text-gray-700">Age <span className="text-red">*</span></label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      required
                      min="0"
                      className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="Years"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Gender <span className="text-red">*</span></label>
                  <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="Male"
                        checked={formData.gender === "Male"}
                        onChange={handleInputChange}
                        required
                        className="w-4 h-4 text-orange focus:ring-orange accent-orange"
                      />
                      <span className="text-gray-700">Male</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="Female"
                        checked={formData.gender === "Female"}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-orange focus:ring-orange accent-orange"
                      />
                      <span className="text-gray-700">Female</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value="Other"
                        checked={formData.gender === "Other"}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-orange focus:ring-orange accent-orange"
                      />
                      <span className="text-gray-700">Other</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Email Address <span className="text-red">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Mobile Number <span className="text-red">*</span></label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 text-gray-500 font-medium">+91</span>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFormData((prev) => ({ ...prev, mobile: val }));
                      }}
                      required
                      pattern="[0-9]{10}"
                      className="w-full bg-[#FAF9F6] border border-gray-200 rounded-r-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="10-digit number"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">WhatsApp Number <span className="text-red">*</span></label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 text-gray-500 font-medium">+91</span>
                    <input
                      type="tel"
                      name="whatsApp"
                      value={formData.whatsApp}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFormData((prev) => ({ ...prev, whatsApp: val }));
                      }}
                      required
                      pattern="[0-9]{10}"
                      className="w-full bg-[#FAF9F6] border border-gray-200 rounded-r-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="10-digit number"
                    />
                  </div>
                </div>
              </div>
            

            {/* Section 2: Guardian & Academic Details */}

              
              <div className="flex items-center gap-3 py-6">
                <div className="p-3 bg-orange/10 rounded-xl text-orange">
                  <MapPin className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-darkred">Guardian & Academic Details</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Parent/Guardian Name <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="guardianName"
                    value={formData.guardianName}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="Enter name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Parent/Guardian Mobile Number <span className="text-red">*</span></label>
                  <div className="flex">
                    <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 text-gray-500 font-medium">+91</span>
                    <input
                      type="tel"
                      name="guardianMobile"
                      value={formData.guardianMobile}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFormData((prev) => ({ ...prev, guardianMobile: val }));
                      }}
                      required
                      pattern="[0-9]{10}"
                      className="w-full bg-[#FAF9F6] border border-gray-200 rounded-r-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                      placeholder="10-digit number"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">City/Town <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="E.g., Vijayawada"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">District <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="E.g., NTR District"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">School / College / Organization Name <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="schoolCollege"
                    value={formData.schoolCollege}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="Enter name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-700">Class / Grade / Course <span className="text-red">*</span></label>
                  <input
                    type="text"
                    name="classGrade"
                    value={formData.classGrade}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                    placeholder="E.g., 10th Standard, B.Tech 2nd Year"
                  />
                </div>
              </div>
            

            
              <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-yellow-500 to-green-500" />
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-orange/10 rounded-xl text-orange">
                  <Trophy className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-darkred">Competition Details</h2>
              </div>

              <div className="space-y-8">
                <div className="space-y-1.5 max-w-md">
                  <label className="text-sm font-bold text-gray-700">Select Category <span className="text-red">*</span></label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-[#FAF9F6] border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 appearance-none text-gray-700"
                  >
                    <option value="" hidden>Choose a category</option>
                    <option value="Children">Children (5–10 Years)</option>
                    <option value="Junior">Junior (11–15 Years)</option>
                    <option value="Senior">Senior (16–25 Years)</option>
                    <option value="Open">Open Category</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-bold text-gray-700">Select Competition(s) <span className="text-red">*</span></label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[
                      "Drawing/Painting/Colouring/Pencil Sketching",
                      "Fancy Dress",
                      "Bhagavad Gita Sloka Recitation",
                      "Storytelling",
                      "Devotional Singing",
                      "Bhajan Jamming",
                      "Classical & Devotional Dance",
                      "Spiritual Quiz",
                      "Elocution",
                      "Rangoli",
                      "Satvik Cooking",
                      "Short Video / Reel Making",
                      "Rath Yatra Model Making/Craft Making",
                      "Bommala Koluvu",
                      "Vegetable & Fruit Carving",
                      "Mono Act / Duologue / Skit / Drama"
                    ].map((comp, idx) => (
                      <label key={idx} className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl hover:bg-orange/5 cursor-pointer transition-colors group">
                        <input
                          type="checkbox"
                          checked={formData.competitions.includes(comp)}
                          onChange={() => handleCompetitionChange(comp)}
                          className="mt-1 w-4 h-4 text-orange rounded border-gray-300 focus:ring-orange accent-orange"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-orange transition-colors">{comp}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="p-6 bg-[#FAF9F6] rounded-2xl border border-gray-200 space-y-6">
                  <div className="space-y-1.5">
                    <label className="text-sm font-bold text-gray-700">Is this a Group Event? <span className="text-red">*</span></label>
                    <div className="flex items-center gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="isGroup"
                          value="Yes"
                          checked={formData.isGroup === "Yes"}
                          onChange={() => {
                            setIsGroupEvent(true);
                            setFormData((prev) => ({ ...prev, isGroup: "Yes" }));
                          }}
                          className="w-4 h-4 text-orange focus:ring-orange accent-orange"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="isGroup"
                          value="No"
                          checked={formData.isGroup === "No"}
                          onChange={() => {
                            setIsGroupEvent(false);
                            setFormData((prev) => ({ ...prev, isGroup: "No" }));
                          }}
                          className="w-4 h-4 text-orange focus:ring-orange accent-orange"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {isGroupEvent && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700">Team Name <span className="text-red">*</span></label>
                        <input
                          type="text"
                          name="teamName"
                          value={formData.teamName}
                          onChange={handleInputChange}
                          required={formData.isGroup === "Yes"}
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                          placeholder="Enter team name"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-bold text-gray-700">Number of Participants <span className="text-red">*</span></label>
                        <input
                          type="number"
                          name="participantsCount"
                          value={formData.participantsCount}
                          onChange={handleInputChange}
                          required={formData.isGroup === "Yes"}
                          min="2"
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20"
                          placeholder="Count"
                        />
                      </div>
                      <div className="space-y-1.5 md:col-span-2">
                        <label className="text-sm font-bold text-gray-700">Names of Team Members <span className="text-red">*</span></label>
                        <textarea
                          name="teamMembers"
                          value={formData.teamMembers}
                          onChange={handleInputChange}
                          required={formData.isGroup === "Yes"}
                          rows="3"
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 resize-none"
                          placeholder="List all team members separated by commas..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            

              {/* Payment Mode Selection */}
              <div className="flex items-center gap-3 py-6 border-t border-gray-100 mt-6">
                <div className="p-3 bg-orange/10 rounded-xl text-orange">
                  <CreditCard className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-darkred">Payment Mode</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <label className={`flex items-center justify-between py-2 px-4 border-2 rounded-2xl cursor-pointer transition-all duration-250 group ${
                  formData.paymentMode === "Online" 
                    ? "border-orange bg-orange/5 shadow-md ring-2 ring-orange/10" 
                    : "border-gray-200 hover:border-orange/40 hover:bg-orange/[0.01]"
                }`}>
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="paymentMode"
                      value="Online"
                      checked={formData.paymentMode === "Online"}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange focus:ring-orange accent-orange cursor-pointer"
                    />
                    <div>
                      <p className="font-bold text-gray-900 group-hover:text-orange transition-colors">Online Payment</p>
                      <p className="text-xs text-gray-500 mt-0.5">Pay via UPI, Cards, NetBanking</p>
                    </div>
                  </div>
                  <CreditCard className="w-6 h-6 text-gray-400 group-hover:text-orange transition-colors" />
                </label>

                <label className={`flex items-center justify-between py-2 px-4 border-2 rounded-2xl cursor-pointer transition-all duration-250 group ${
                  formData.paymentMode === "Cash" 
                    ? "border-orange bg-orange/5 shadow-md ring-2 ring-orange/10" 
                    : "border-gray-200 hover:border-orange/40 hover:bg-orange/[0.01]"
                }`}>
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="paymentMode"
                      value="Cash"
                      checked={formData.paymentMode === "Cash"}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange focus:ring-orange accent-orange cursor-pointer"
                    />
                    <div>
                      <p className="font-bold text-gray-900 group-hover:text-orange transition-colors">Cash Payment</p>
                      <p className="text-xs text-gray-500 mt-0.5">Pay cash directly at the center</p>
                    </div>
                  </div>
                  <span className="text-2xl filter group-hover:scale-110 transition-transform">💵</span>
                </label>
              </div>

            
              <div className="flex items-center gap-3 py-6">
                <div className="p-2.5 bg-orange text-white rounded-xl shadow-md">
                  <CheckSquare className="w-5 h-5" />
                </div>
                <h2 className="text-xl font-bold text-darkred">Declaration <span className="text-red">*</span></h2>
              </div>
              
              <div className="space-y-4 mb-8">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="declaration1"
                    checked={formData.declaration1}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-5 h-5 text-orange rounded border-orange/30 focus:ring-orange accent-orange"
                  />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 leading-relaxed">I hereby confirm that the information provided is true and correct.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="declaration2"
                    checked={formData.declaration2}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-5 h-5 text-orange rounded border-orange/30 focus:ring-orange accent-orange"
                  />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 leading-relaxed">I agree to abide by the rules and regulations of Rath Fest 2026.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="declaration3"
                    checked={formData.declaration3}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-5 h-5 text-orange rounded border-orange/30 focus:ring-orange accent-orange"
                  />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 leading-relaxed">I understand that the judges' decision will be final.</span>
                </label>
              </div>

              <div className="flex justify-center pt-6 border-t border-orange/20">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-red text-white font-bold text-lg px-12 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Processing..." : formData.paymentMode === "Cash" ? "Submit Registration" : "Pay for Registration"}
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>
      <Footer />

      {/* Success Modal Overlay */}
      <AnimatePresence>
        {showSuccessModal && (
          <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-amber-500/20"
            >
              {/* Premium Top Strip */}
              <div className="h-2 bg-gradient-to-r from-red via-orange to-yellow-500 w-full" />
              
              <div className="p-8 text-center space-y-6 text-gray-800">
                
                {/* Golden Animated Mandala Circle */}
                <div className="w-12 h-12 bg-orange/10 text-orange rounded-full flex items-center justify-center mx-auto shadow-inner relative">
                  <Check className="w-6 h-6 text-orange" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                    className="absolute inset-0 border border-dashed border-orange rounded-full"
                  />
                </div>

                <div className="space-y-2">
                  <h2 className="font-dm-serif text-3xl font-black text-darkred">Registration Successful!</h2>
                  <p className="text-sm text-gray-500">Hare Krishna! Your registration details for Rath Fest 2026 have been stored successfully.</p>
                </div>

                {/* Registration details container */}
                <div className="bg-[#FAF9F6] border border-orange/10 rounded-2xl p-5 text-left space-y-4 shadow-inner">
                  <div className="border-b border-orange/10 pb-2.5 flex justify-between items-center text-sm font-bold text-darkred">
                    <span>Participant Details</span>
                    <span className="text-xs text-orange bg-orange/5 px-2 py-0.5 rounded-md font-semibold">Category: {formData.category || "N/A"}</span>
                  </div>
                  
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong className="text-gray-900">Name:</strong> {formData.fullName}</p>
                    <p><strong className="text-gray-900">Age / Gender:</strong> {formData.age} Years / {formData.gender}</p>
                    <p><strong className="text-gray-900">Mobile:</strong> +91 {formData.mobile}</p>
                    <p><strong className="text-gray-900">Email:</strong> {formData.email}</p>
                    <p><strong className="text-gray-900">School / College:</strong> {formData.schoolCollege} ({formData.classGrade})</p>
                    <p><strong className="text-gray-900">City / District:</strong> {formData.city}, {formData.district}</p>
                  </div>

                  <div className="border-t border-orange/10 pt-3 space-y-2">
                    <p className="text-sm font-bold text-darkred">Selected Competition(s):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {formData.competitions.map((comp, idx) => (
                        <span key={idx} className="text-xs bg-orange/5 text-orange border border-orange/10 rounded-full px-3 py-1 font-medium">
                          {comp}
                        </span>
                      ))}
                    </div>
                  </div>

                  {formData.isGroup === "Yes" && (
                    <div className="border-t border-orange/10 pt-3 space-y-2">
                      <p className="text-sm font-bold text-darkred">Group Registration Details:</p>
                      <div className="space-y-1.5 text-xs text-gray-700 bg-white border border-gray-100 rounded-xl p-3">
                        <p><strong className="text-gray-900">Team Name:</strong> {formData.teamName}</p>
                        <p><strong className="text-gray-900">Members Count:</strong> {formData.participantsCount}</p>
                        <p><strong className="text-gray-900 font-semibold">Members:</strong> {formData.teamMembers}</p>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-orange/10 pt-3 flex justify-between items-center text-xs text-gray-500">
                    <span>Payment ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-gray-700">{paymentId || "N/A"}</code></span>
                    <span className="font-bold text-orange text-sm">Status: {formData.paymentMode === "Cash" ? "Cash" : "Paid"}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 leading-relaxed max-w-sm mx-auto">
                  A verification confirmation and event schedule updates will be sent to your mobile number. May Lord Jagannath bless you.
                </p>

                <button
                  type="button"
                  onClick={handleModalClose}
                  className="w-full bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-orange text-white font-extrabold py-3.5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 text-sm uppercase tracking-wide border-0 outline-none"
                >
                  Jai Jagannath!
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RathFestRegistration;